import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { toast } from 'sonner'
import { z } from 'zod'

import { queryKeys } from '@/constants/queryKeys'
import { useErrorToast } from '@/hooks/useErrorToast'
import { parseDateFromBE, parseToFormat } from '@/lib/date'
import { parseFullPhoneNumber } from '@/lib/phone'
import { queryClient } from '@/lib/queryClient'
import { getFullName } from '@/lib/typography'
import { removeEmptyFormFields } from '@/lib/utils'
import {
  CountryCodeField,
  CountryCombobox,
  OptionalTag,
  Widget,
} from '@/shared/components'
import {
  AnimatedFormLabel,
  Button,
  DatePickerInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  MotionDiv,
  PhoneNumberInput,
  Sheet,
  SheetContent,
  SlideInLeft,
  StickyContainer,
  Typography,
} from '@/shared/ui'

import { updateContractor } from '../../api'
import { ContractorDetails, ContractorState } from '../../types'

const EDIT_CONTRACTOR_FORM = 'edit-contractor-form'

const editContractorSchema = z.object({
  firstName: z.string().min(1, {
    message: 'validation.firstName.required',
  }),
  lastName: z.string().min(1, {
    message: 'validation.lastName.required',
  }),
  secondLastName: z.string().optional(),
  email: z.string().email({
    message: 'validation.email.invalid',
  }),
  internationalPhonePrefix: z.string().min(1, {
    message: 'validation.phoneCode.required',
  }),
  localPhoneNumber: z
    .string()
    .min(1, {
      message: 'validation.phoneNumber.required',
    })
    .refine(isValidPhoneNumber, {
      message: 'validation.phoneNumber.invalid',
    }),
  dateOfBirth: z.string().min(1, {
    message: 'validation.dateOfBirth.required',
  }),
  residentialAddress: z.object({
    addressStreet: z
      .string()
      .min(1, { message: 'validation.addressStreet.required' }),
    addressStreetNumber: z
      .string()
      .min(1, { message: 'validation.addressStreetNumber.required' }),
    addressDistrict: z.string().optional(),
    addressCity: z
      .string()
      .min(1, { message: 'validation.addressCity.required' }),
    addressState: z
      .string()
      .min(1, { message: 'validation.addressState.required' }),
    addressPostCode: z
      .string()
      .min(1, { message: 'validation.addressZipCode.required' }),
    addressCountry: z
      .string()
      .min(1, { message: 'validation.addressCountry.required' }),
  }),
})

type EditContractorSchema = z.infer<typeof editContractorSchema>

type Props = {
  contractor?: ContractorDetails
  isOpen: boolean
  onOpenChange: () => void
}

export const ContractorPersonalInformationEdit = ({
  contractor,
  isOpen,
  onOpenChange,
}: Props) => {
  const intl = useIntl()
  const notifyError = useErrorToast()

  const form = useForm<EditContractorSchema>({
    mode: 'onChange',
    resolver: zodResolver(editContractorSchema),
    values: {
      firstName: contractor?.personalDetails?.firstName ?? '',
      lastName: contractor?.personalDetails?.lastName ?? '',
      secondLastName: contractor?.personalDetails?.secondLastName ?? '',
      email: contractor?.personalDetails?.email ?? '',
      internationalPhonePrefix:
        contractor?.personalDetails?.internationalPhonePrefix ?? '',
      localPhoneNumber:
        contractor?.personalDetails?.localPhoneNumber &&
        contractor?.personalDetails?.internationalPhonePrefix
          ? contractor?.personalDetails?.internationalPhonePrefix +
            contractor?.personalDetails?.localPhoneNumber
          : '',
      dateOfBirth: contractor?.personalDetails?.dateOfBirth
        ? parseDateFromBE(contractor.personalDetails.dateOfBirth)
        : '',
      residentialAddress: {
        addressStreet:
          contractor?.personalDetails?.residentialAddress?.addressStreet ?? '',
        addressStreetNumber:
          contractor?.personalDetails?.residentialAddress
            ?.addressStreetNumber ?? '',
        addressDistrict:
          contractor?.personalDetails?.residentialAddress?.addressDistrict ??
          '',
        addressCity:
          contractor?.personalDetails?.residentialAddress?.addressCity ?? '',
        addressState:
          contractor?.personalDetails?.residentialAddress?.addressState ?? '',
        addressPostCode:
          contractor?.personalDetails?.residentialAddress?.addressPostCode ??
          '',
        addressCountry:
          contractor?.personalDetails?.residentialAddress?.addressCountry ?? '',
      },
    },
  })

  const {
    mutateAsync: updateContractorMutation,
    isPending,
    isSuccess,
    reset,
  } = useMutation({
    mutationFn: updateContractor,
  })

  const onSubmit: SubmitHandler<EditContractorSchema> = async (data) => {
    if (!contractor?.id) {
      toast.error('Contractor ID not found')
      return
    }

    removeEmptyFormFields(data)

    try {
      await updateContractorMutation({
        ...data,
        contractorId: contractor.id,
        localPhoneNumber: parseFullPhoneNumber(data),
        dateOfBirth: parseToFormat(data.dateOfBirth),
      })

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.getContractor, contractor.id],
      })

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.getContractors],
      })

      toast.success(
        intl.formatMessage({
          id: 'contractor.update.success',
          defaultMessage: 'Contractor has been updated successfully',
        }),
      )

      onOpenChange()

      reset()
    } catch (error) {
      notifyError(error)
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent>
        <SlideInLeft className="flex min-h-screen flex-col">
          <div className="flex flex-col gap-1">
            <Typography variant="h3">
              <FormattedMessage
                id="contractor.edit.personalInformation.title"
                defaultMessage="Edit personal information"
              />
            </Typography>

            <Typography className="text-neutral-gray-600">
              <FormattedMessage
                id="contractor.edit.personalInformation.description"
                defaultMessage="Edit {fullName} personal information"
                values={{ fullName: getFullName(contractor?.personalDetails) }}
              />
            </Typography>
          </div>

          <div className="p-4" />

          <Form {...form}>
            <form
              id={EDIT_CONTRACTOR_FORM}
              className="flex h-full flex-col"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="flex flex-col gap-6">
                <Widget
                  variant="form"
                  title={
                    <FormattedMessage
                      id="label.personalDetails"
                      defaultMessage="Personal details"
                    />
                  }
                >
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            autoComplete="given-name"
                            placeholder={intl.formatMessage({
                              defaultMessage: 'First name',
                              id: 'label.firstName',
                            })}
                            {...field}
                          />
                        </FormControl>
                        <AnimatedFormLabel>
                          <FormattedMessage
                            defaultMessage="First name"
                            id="label.firstName"
                          />
                        </AnimatedFormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            autoComplete="family-name"
                            placeholder={intl.formatMessage({
                              defaultMessage: 'Last name',
                              id: 'label.lastName',
                            })}
                            {...field}
                          />
                        </FormControl>
                        <AnimatedFormLabel>
                          <FormattedMessage
                            defaultMessage="Last name"
                            id="label.lastName"
                          />
                        </AnimatedFormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="secondLastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            autoComplete="additional-name"
                            placeholder={intl.formatMessage({
                              defaultMessage: 'Second last name',
                              id: 'label.secondLastName',
                            })}
                            {...field}
                          />
                        </FormControl>
                        <AnimatedFormLabel>
                          <FormattedMessage
                            defaultMessage="Second last name"
                            id="label.secondLastName"
                          />
                        </AnimatedFormLabel>
                        {field.value === '' && <OptionalTag />}
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="dateOfBirth"
                    render={({ field }) => (
                      <FormItem>
                        <DatePickerInput
                          placeholder={intl.formatMessage({
                            defaultMessage: 'Date of birth',
                            id: 'label.dateOfBirth',
                          })}
                          {...field}
                        />
                      </FormItem>
                    )}
                  />
                </Widget>

                <Widget
                  variant="form"
                  title={
                    <FormattedMessage
                      id="label.residentialAddress"
                      defaultMessage="Residential address"
                    />
                  }
                >
                  <FormField
                    control={form.control}
                    name="residentialAddress.addressCountry"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <CountryCombobox
                            placeholder={intl.formatMessage({
                              id: 'label.country',
                              defaultMessage: 'Country',
                            })}
                            onSelect={(value) => {
                              field.onChange(value.valueAsCode)
                            }}
                            value={field.value}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {form.watch('residentialAddress.addressCountry') !== '' ? (
                    <MotionDiv className="flex flex-col gap-3">
                      <div className="grid grid-cols-2 gap-3">
                        <FormField
                          control={form.control}
                          name="residentialAddress.addressStreet"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  autoComplete="address-line1"
                                  placeholder={intl.formatMessage({
                                    id: 'label.street',
                                    defaultMessage: 'Street',
                                  })}
                                  {...field}
                                />
                              </FormControl>
                              <AnimatedFormLabel>
                                <FormattedMessage
                                  defaultMessage="Street"
                                  id="label.street"
                                />
                              </AnimatedFormLabel>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="residentialAddress.addressStreetNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  autoComplete="address-line2"
                                  placeholder={intl.formatMessage({
                                    id: 'label.streetNumber',
                                    defaultMessage: 'Street number',
                                  })}
                                  {...field}
                                />
                              </FormControl>
                              <AnimatedFormLabel>
                                <FormattedMessage
                                  defaultMessage="Street number"
                                  id="label.streetNumber"
                                />
                              </AnimatedFormLabel>
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="residentialAddress.addressDistrict"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                placeholder={intl.formatMessage({
                                  id: 'label.neighborhoodOrDistrict',
                                  defaultMessage: 'Neighborhood or district',
                                })}
                                {...field}
                              />
                            </FormControl>
                            <AnimatedFormLabel>
                              <FormattedMessage
                                id="label.neighborhoodOrDistrict"
                                defaultMessage="Neighborhood or district"
                              />
                            </AnimatedFormLabel>
                            {field.value === '' && <OptionalTag />}
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="residentialAddress.addressCity"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                key="addressCity"
                                autoComplete="address-level2"
                                placeholder={intl.formatMessage({
                                  id: 'label.city',
                                  defaultMessage: 'City',
                                })}
                                {...field}
                              />
                            </FormControl>
                            <AnimatedFormLabel>
                              <FormattedMessage
                                defaultMessage="City"
                                id="label.city"
                              />
                            </AnimatedFormLabel>
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-2 gap-3">
                        <FormField
                          control={form.control}
                          name="residentialAddress.addressState"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  autoComplete="address-level1"
                                  placeholder={intl.formatMessage({
                                    id: 'label.state',
                                    defaultMessage: 'State',
                                  })}
                                  {...field}
                                />
                              </FormControl>
                              <AnimatedFormLabel>
                                <FormattedMessage
                                  defaultMessage="State"
                                  id="label.state"
                                />
                              </AnimatedFormLabel>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="residentialAddress.addressPostCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  autoComplete="postal-code"
                                  placeholder={intl.formatMessage({
                                    id: 'label.postCode',
                                    defaultMessage: 'Post code',
                                  })}
                                  {...field}
                                />
                              </FormControl>
                              <AnimatedFormLabel>
                                <FormattedMessage
                                  defaultMessage="Post code"
                                  id="label.postCode"
                                />
                              </AnimatedFormLabel>
                            </FormItem>
                          )}
                        />
                      </div>
                    </MotionDiv>
                  ) : null}
                </Widget>

                <Widget
                  variant="form"
                  title={
                    <FormattedMessage
                      id="label.contactDetails"
                      defaultMessage="Contact details"
                    />
                  }
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            disabled={
                              contractor?.state !==
                              ContractorState.WAITING_FOR_CONTRACTOR
                            }
                            autoComplete="email"
                            placeholder={intl.formatMessage({
                              defaultMessage: 'Email',
                              id: 'label.email',
                            })}
                            {...field}
                          />
                        </FormControl>
                        <AnimatedFormLabel>
                          <FormattedMessage
                            defaultMessage="Email"
                            id="label.email"
                          />
                        </AnimatedFormLabel>
                      </FormItem>
                    )}
                  />

                  <div className="flex gap-3">
                    <FormField
                      control={form.control}
                      name="internationalPhonePrefix"
                      render={({ field }) => {
                        return (
                          <CountryCodeField
                            value={field.value}
                            onSelect={(value) =>
                              form.setValue('internationalPhonePrefix', value)
                            }
                          />
                        )
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="localPhoneNumber"
                      render={({ field }) => (
                        <FormItem className="relative flex-1">
                          <FormControl>
                            <PhoneNumberInput
                              phonePrefix={form.watch(
                                'internationalPhonePrefix',
                              )}
                              placeholder={intl.formatMessage({
                                defaultMessage: 'Contact phone number',
                                id: 'label.contactPhoneNumber',
                              })}
                              {...field}
                            />
                          </FormControl>
                          <AnimatedFormLabel>
                            <FormattedMessage
                              defaultMessage="Contact phone number"
                              id="label.contactPhoneNumber"
                            />
                          </AnimatedFormLabel>
                        </FormItem>
                      )}
                    />
                  </div>
                </Widget>
              </div>
            </form>
          </Form>

          <div className="p-4" />

          <StickyContainer className="-bottom-16">
            <Button
              width="full"
              form={EDIT_CONTRACTOR_FORM}
              onClick={form.handleSubmit(onSubmit)}
              loading={isPending || isSuccess}
              disabled={
                !form.formState.isValid ||
                isPending ||
                !form.formState.isDirty ||
                isSuccess
              }
              type="submit"
            >
              <FormattedMessage
                defaultMessage="Save changes"
                id="action.saveChanges"
              />
            </Button>
          </StickyContainer>
        </SlideInLeft>
      </SheetContent>
    </Sheet>
  )
}
